import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { finalize } from 'rxjs';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { HttpService } from '../../http/http.service';
import { ConstantUtil } from '../../util/constant.util';

// Instagram 頁面接口
interface InstagramPage {
  id: string;
  userName?: string;
  name: string;
  accessToken: string;
  pictureUrl?: string;
}

@Component({
  selector: 'app-instagram-callback',
  templateUrl: './instagram-callback.component.html',
  styleUrls: ['./instagram-callback.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzButtonModule,
    NzAvatarModule,
    NzIconModule,
    NzTableModule,
    NzRadioModule,
    TestIdDirective,
    I18nPipe
  ]
})
export class InstagramCallbackComponent implements OnInit {
  private readonly message = inject(NzMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = true;
  error: string | null = null;
  instagramPages: InstagramPage[] = [];
  selectedPageId: string | null = null;
  accessToken: string | null = null;
  processingStatus: 'loading' | 'user_loaded' | 'error' = 'loading';

  private readonly serviceNumberId: string | null = null;
  private bindLoading = false;
  private messageSent = false;

  constructor(private http: HttpService) {
    // 從 localStorage 獲取服務號 ID
    this.serviceNumberId = localStorage.getItem('ig_binding_service_number_id');

    if (!this.serviceNumberId) {
      this.processingStatus = 'error';
      this.error = this.i18n.fanyi('tenant.channel.callback.service-number-not-found');
      this.loading = false;
    }
  }

  ngOnInit(): void {
    this.handleInstagramCallback();
  }

  private async handleInstagramCallback(): Promise<void> {
    try {
      const urlParams = this.parseUrlParameters();

      if (urlParams.error) {
        this.error = `${this.i18n.fanyi('tenant.channel.callback.instagram-auth-failed')}: ${urlParams.error_description || urlParams.error}`;
        this.loading = false;
        this.sendResultToParent({
          success: false,
          error: this.error
        });
        return;
      }

      if (urlParams.code) {
        // Instagram OAuth返回的是授权码，需要先交换为短期token
        const authCode = urlParams.code;

        try {
          this.getIGUserInfo(authCode);
        } catch (error) {
          console.error('Token exchange failed:', error);
          this.error = `${this.i18n.fanyi('tenant.channel.callback.token-exchange-failed')}: ${error}`;
          this.loading = false;
          this.processingStatus = 'error';
        }
      } else {
        this.error = this.i18n.fanyi('tenant.channel.callback.no-access-token');
        this.loading = false;
        this.sendResultToParent({
          success: false,
          error: this.error
        });
      }
    } catch (error: any) {
      console.error('Instagram callback error:', error);
      this.error = error.message || this.i18n.fanyi('tenant.channel.callback.instagram-callback-error');
      this.loading = false;

      // 发送错误信息给父窗口
      this.sendResultToParent({
        success: false,
        error: this.error
      });
    }
  }

  private parseUrlParameters(): any {
    const params: any = {};

    // 解析 hash 參數
    const hash = window.location.hash.substring(1);
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      hashParams.forEach((value, key) => {
        params[key] = value;
      });
    }

    // 解析 search 參數
    const search = window.location.search.substring(1);
    if (search) {
      const searchParams = new URLSearchParams(search);
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
    }

    // 處理特殊格式 ?#access_token=xxx
    const fullUrl = window.location.href;
    const specialMatch = fullUrl.match(/\?#(.+)/);
    if (specialMatch) {
      const specialParams = new URLSearchParams(specialMatch[1]);
      specialParams.forEach((value, key) => {
        params[key] = value;
      });
    }

    const sanitizedParams = { ...params };
    if (sanitizedParams.access_token) {
      sanitizedParams.access_token = '[REDACTED]';
    }
    console.log('Parsed URL parameters:', sanitizedParams);
    return params;
  }

  private getIGUserInfo(code: string) {
    // 使用当前环境的重定向URL
    const redirectUrl = `${window.location.origin}/checkIGLogin`;
    //  const redirectUrl = 'https://aile-admin-dev.web.app/checkIGLogin';

    // 调用后端接口进行Instagram认证
    const requestData = {
      authCode: code,
      redirectUri: redirectUrl
    };
    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_CHANNEL_INSTAGRAM_AUTH, requestData)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.accessToken = res.data.accessToken;
            this.processingStatus = 'user_loaded';
            this.loading = false;
            if (res.data) {
              this.instagramPages = [
                {
                  id: res.data.id,
                  name: res.data.name,
                  userName: res.data.userName,
                  pictureUrl: res.data.pictureUrl,
                  accessToken: res.data.accessToken
                }
              ];
            }
          } else {
            this.error = res.msg || this.i18n.fanyi('tenant.channel.callback.instagram-auth-failed');
            this.processingStatus = 'error';
            this.message.error(this.error || this.i18n.fanyi('tenant.channel.callback.instagram-auth-failed'));
          }
        },
        error: error => {
          console.error('Instagram auth error:', error);
          this.error = this.i18n.fanyi('tenant.channel.callback.instagram-auth-process-error');
          this.processingStatus = 'error';
          this.message.error(this.i18n.fanyi('tenant.channel.callback.instagram-auth-process-error'));
        }
      });
  }

  private sendResultToParent(result: any): void {
    // 防护：防止重复发送消息
    if (this.messageSent) {
      console.log('Message already sent, ignoring duplicate send attempt');
      return;
    }

    const parentOrigin = sessionStorage.getItem('parent_origin') || window.location.origin;

    if (window.opener) {
      this.messageSent = true;
      window.opener.postMessage(result, parentOrigin);
      console.log('Result sent to parent, success:', result?.success);

      // 清理sessionStorage
      sessionStorage.removeItem('instagram_oauth_state');
      sessionStorage.removeItem('parent_origin');

      // 关闭弹窗
      setTimeout(() => {
        window.close();
      }, 1000);
    }
  }

  onRetry(): void {
    this.loading = true;
    this.error = null;
    this.handleInstagramCallback();
  }

  onClose(): void {
    this.sendResultToParent({
      success: false,
      error: this.i18n.fanyi('tenant.channel.callback.user-cancelled')
    });
  }

  confirmSelection(): void {
    if (!this.selectedPageId || this.bindLoading || !this.serviceNumberId) {
      return;
    }

    const selectedPage = this.instagramPages.find(page => page.id === this.selectedPageId);
    if (!selectedPage) {
      this.message.error(this.i18n.fanyi('tenant.channel.callback.select-instagram-page'));
      return;
    }

    this.bindLoading = true;

    // 發送選中的頁面數據到父窗口，與Facebook callback保持一致
    this.sendResultToParent({
      type: 'PAGE_SELECTED',
      pageData: selectedPage,
      channelType: 'Instagram',
      serviceNumberId: this.serviceNumberId
    });

    // 關閉窗口
    setTimeout(() => {
      window.close();
    }, 1000);
  }

  closeWindow(): void {
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'INSTAGRAM_LOGIN_ERROR',
          error: this.i18n.fanyi('tenant.channel.callback.user-cancelled')
        },
        window.location.origin
      );
    }
    window.close();
  }
}
